/**
 * Workspace Service
 * Handles workspace management and user relationships
 */

import { Workspace, WorkspaceFormData } from '@/types'
import { BaseService } from './base.service'
import { StorageKeys } from './storage'
import { cleanUser } from '@/lib/utils/clean-references'

class WorkspaceService extends BaseService<Workspace> {
  constructor() {
    super(StorageKeys.WORKSPACES)
  }

  /**
   * Create workspace with creator
   */
  async createWorkspace(data: WorkspaceFormData, creatorId: string): Promise<Workspace> {
    const { userService } = await import('./user.service')
    const creator = await userService.getById(creatorId)

    if (!creator) {
      throw new Error('Creator not found')
    }

    // Clean user to prevent circular reference issues
    return this.create({
      ...data,
      creator: cleanUser(creator),
      users: [cleanUser(creator)],
      projects: [],
      tasks: [],
      messages: [],
      documents: [],
    })
  }

  /**
   * Add user to workspace
   */
  async addUser(workspaceId: string, userId: string): Promise<Workspace> {
    const workspace = await this.getById(workspaceId)
    if (!workspace) {
      throw new Error('Workspace not found')
    }

    const { userService } = await import('./user.service')
    const user = await userService.getById(userId)
    if (!user) {
      throw new Error('User not found')
    }

    // Check if user already in workspace
    if (workspace.users.some((u) => u.id === userId)) {
      throw new Error('User already in workspace')
    }

    // Clean user to prevent circular reference issues
    return this.update(workspaceId, {
      users: [...workspace.users, cleanUser(user)],
    } as Partial<Workspace>)
  }

  /**
   * Remove user from workspace
   */
  async removeUser(workspaceId: string, userId: string): Promise<Workspace> {
    const workspace = await this.getById(workspaceId)
    if (!workspace) {
      throw new Error('Workspace not found')
    }

    // Can't remove creator
    if (workspace.creator.id === userId) {
      throw new Error('Cannot remove workspace creator')
    }

    return this.update(workspaceId, {
      users: workspace.users.filter((u) => u.id !== userId),
    } as Partial<Workspace>)
  }

  /**
   * Get workspaces for user with populated counts
   * SuperAdmins can see all workspaces
   */
  async getWorkspacesForUser(userId: string): Promise<Workspace[]> {
    const { userService } = await import('./user.service')
    const user = await userService.getById(userId)

    if (!user) {
      return []
    }

    const allWorkspaces = await this.getAll()

    // SuperAdmins can see all workspaces
    if (user.role === 'SuperAdmin') {
      // Populate all related nodes for SuperAdmins
      const workspacesWithCounts = await Promise.all(
        allWorkspaces.map(async (workspace) => {
          const { projectService } = await import('./project.service')
          const { taskService } = await import('./task.service')
          const { messageService } = await import('./message.service')
          const { documentService } = await import('./document.service')

          const [projects, tasks, messages, documents] = await Promise.all([
            projectService.getProjectsForWorkspace(workspace.id),
            taskService.getTasksForWorkspace(workspace.id),
            messageService.getMessagesForWorkspace(workspace.id),
            documentService.getDocumentsForWorkspace(workspace.id),
          ])

          return {
            ...workspace,
            projects: projects || [],
            tasks: tasks || [],
            messages: messages || [],
            documents: documents || [],
          }
        })
      )
      return workspacesWithCounts
    }

    // Regular users only see workspaces they're members of
    const filtered = await this.filter((workspace) => workspace.users.some((u) => u.id === userId))

    // Populate related nodes for regular users
    const workspacesWithCounts = await Promise.all(
      filtered.map(async (workspace) => {
        const { projectService } = await import('./project.service')
        const { taskService } = await import('./task.service')
        const { messageService } = await import('./message.service')
        const { documentService } = await import('./document.service')

        const [projects, tasks, messages, documents] = await Promise.all([
          projectService.getProjectsForWorkspace(workspace.id),
          taskService.getTasksForWorkspace(workspace.id),
          messageService.getMessagesForWorkspace(workspace.id),
          documentService.getDocumentsForWorkspace(workspace.id),
        ])

        return {
          ...workspace,
          projects: projects || [],
          tasks: tasks || [],
          messages: messages || [],
          documents: documents || [],
        }
      })
    )
    return workspacesWithCounts
  }

  /**
   * Get workspace members
   */
  async getMembers(workspaceId: string): Promise<Workspace['users']> {
    const workspace = await this.getById(workspaceId)
    return workspace?.users || []
  }

  /**
   * Search workspaces
   */
  async searchWorkspaces(query: string): Promise<Workspace[]> {
    return this.search(query, ['name', 'email'])
  }

  /**
   * Check if user is member
   */
  async isMember(workspaceId: string, userId: string): Promise<boolean> {
    const workspace = await this.getById(workspaceId)
    return workspace?.users.some((u) => u.id === userId) ?? false
  }

  /**
   * Get workspace by ID with populated projects, tasks, messages, and documents
   * This ensures projects, tasks, messages, and documents are always loaded from their respective storage
   */
  async getByIdWithTasks(workspaceId: string): Promise<Workspace | null> {
    const workspace = await this.getById(workspaceId)
    if (!workspace) return null

    // Get projects from project storage
    const { projectService } = await import('./project.service')
    const projects = await projectService.getProjectsForWorkspace(workspaceId)

    // Get tasks from task storage
    const { taskService } = await import('./task.service')
    const tasks = await taskService.getTasksForWorkspace(workspaceId)

    // Get messages from message storage
    const { messageService } = await import('./message.service')
    const messages = await messageService.getMessagesForWorkspace(workspaceId)

    // Get documents from document storage
    const { documentService } = await import('./document.service')
    const documents = await documentService.getDocumentsForWorkspace(workspaceId)

    return {
      ...workspace,
      projects: projects || [],
      tasks: tasks || [],
      messages: messages || [],
      documents: documents || [],
    }
  }
}

export const workspaceService = new WorkspaceService()
